import { useCallback, useEffect, useMemo, useState } from 'react';
import { useField } from 'formik';
import { TeamOutlined } from '@ant-design/icons';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import { ManifestFormFields, ManifestFormSteps } from '../../types';
import FormAutocomplete from 'components/new-ui/composed/formAutocomplete/FormAutocomplete';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import { TeamStatuses, type GetTeamsParams, type Team } from 'store/teams/types';
import { debounce } from 'lodash';
import Notification from 'components/ui/base/notification/Notification';
import { listTeams } from 'store/teams/client';
import { DefaultOptionType } from 'antd/lib/select';

const fieldName = 'team.teamId';

const defaultParams: GetTeamsParams = {
    page: 0,
    size: 100,
    status: TeamStatuses.ACTIVE,
};

const TeamSection = () => {
    const [field, meta, helpers] = useField(fieldName);
    const { isActive, isComplete, isIdle, resetStep, currentStepConfig, lastResetEvent, stepsConfig } =
        useMultiStepFormContext<ManifestFormFields>();

    const [teams, setTeams] = useState<Team[]>([]);
    const [teamInput, setTeamInput] = useState('');

    const stepKey = ManifestFormSteps.TEAM;
    const sectionTitle = 'Team';
    const disabled = currentStepConfig?.disabled ?? false;
    const isActiveStep = isActive(stepKey);
    const currentStepIndex = useMemo(() => stepsConfig.findIndex((s) => s.key === stepKey), [stepKey, stepsConfig]);

    const fetchTeams = useCallback(
        async (query?: string) => {
            try {
                const params: GetTeamsParams = {
                    ...defaultParams,
                    page: 0,
                    ...(query?.trim() ? { query: query.trim() } : {}),
                };
                const res = await listTeams(params);
                setTeams(res.list ?? []);
            } catch (err) {
                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Error',
                    description: 'Failed to load teams',
                });
                setTeams([]);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [field.value]
    );

    const debouncedFetchTeams = useMemo(() => debounce((q: string) => fetchTeams(q), 300), [fetchTeams]);

    const teamsOptions: DefaultOptionType[] = useMemo(() => {
        if (!teams) {
            return [];
        }

        // Note: For some reason, when getting teams by query, the BE returns duplicates
        const teamsMap = new Map(teams.map((t) => [t.id, t]));
        const uniqueTeams = Array.from(teamsMap.values());

        return uniqueTeams.map((t) => ({
            label: t.name,
            value: t.id,
        }));
    }, [teams]);

    const handleReset = () => {
        resetStep(stepKey, 'teamId');
        setTeamInput('');
    };

    const handleSelectTeam = (val: string, option?: DefaultOptionType) => {
        setTeamInput(String(option?.label ?? ''));
        helpers.setValue(val);
        debouncedFetchTeams.cancel();
    };

    const handleClearTeam = () => {
        setTeamInput('');
        helpers.setValue(undefined);
    };

    const handleSearchTeams = (val: string) => {
        setTeamInput(val);
        debouncedFetchTeams(val);
    };

    useEffect(() => {
        if (isActiveStep) {
            fetchTeams();
        }

        return () => debouncedFetchTeams.cancel();
    }, [debouncedFetchTeams, fetchTeams, isActiveStep]);

    // Reset the input if the previous section has been reset
    useEffect(() => {
        if (!lastResetEvent?.stepIndex) return;

        if (lastResetEvent.stepIndex > -1 && lastResetEvent.stepIndex < currentStepIndex) {
            setTeamInput('');
            helpers.setValue(undefined);
            debouncedFetchTeams.cancel();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResetEvent, currentStepIndex]);

    if (isIdle(stepKey)) {
        return <FormSectionHeader title={sectionTitle} icon={<TeamOutlined />} />;
    }

    if (isActive(stepKey)) {
        return (
            <div>
                <FormSectionHeader title={sectionTitle} icon={<TeamOutlined />} subtitle="Select a team" />
                <FormAutocomplete
                    disabled={disabled}
                    fieldName={fieldName}
                    inputPlaceholder="Search team"
                    style={{ width: '100%' }}
                    fieldMeta={meta}
                    options={teamsOptions}
                    onSearch={handleSearchTeams}
                    onSelect={handleSelectTeam}
                    onClear={handleClearTeam}
                    onBlur={() => helpers.setTouched(true)}
                    value={teamInput}
                />
            </div>
        );
    }

    if (isComplete(stepKey)) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <FormSectionHeader title={sectionTitle} icon={<TeamOutlined />} />
                <ReadonlyFormItem disabled={disabled} value={teamInput} onReset={handleReset} />
            </div>
        );
    }

    return <div>Team</div>;
};

export default TeamSection;
